import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { API_BASE } from '../config/api';
import { resolveSession } from '../lib/session';
import { useSessionUser } from '../SessionContext';
import './Reports.css';

const PAGE_SIZE = 50;

const toIsoDate = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const initialFilters = () => {
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    startDate: toIsoDate(monthStart),
    endDate: toIsoDate(today),
    type: '',
    language: '',
    callerNumber: '',
  };
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const readResponse = async (response) => {
  const text = await response.text();
  let payload = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }
  if (!response.ok) {
    throw new Error(payload?.message || text || `Request failed (${response.status})`);
  }
  return payload;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return '-';
  const total = Math.max(0, Number(seconds) || 0);
  const minutes = Math.floor(total / 60);
  const remaining = Math.floor(total % 60);
  return `${String(minutes).padStart(2, '0')}:${String(remaining).padStart(2, '0')}`;
};

const QueueContactOptionReport = () => {
  const navigate = useNavigate();
  const { user: sessionUser } = useSessionUser();
  const { username, roleName } = useMemo(() => resolveSession(sessionUser), [sessionUser]);
  const [filters, setFilters] = useState(initialFilters);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, callback: 0, voicemail: 0, recorded: 0, languages: {} });
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  const [reportInterval, setReportInterval] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.body.setAttribute('data-app', 'reports');
    return () => document.body.removeAttribute('data-app');
  }, []);

  const loadReport = useCallback(async (nextFilters, page = 1) => {
    if (!nextFilters.startDate || !nextFilters.endDate) {
      setError('Start Date and End Date are required.');
      return;
    }

    const params = new URLSearchParams({
      startDate: nextFilters.startDate,
      endDate: nextFilters.endDate,
      page: String(page),
      perPage: String(PAGE_SIZE),
    });
    ['type', 'language', 'callerNumber'].forEach((key) => {
      const value = String(nextFilters[key] || '').trim();
      if (value) params.set(key, value);
    });

    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/reports/queue-contact-options?${params.toString()}`, {
        headers: authHeaders(),
      });
      const payload = await readResponse(response);
      setRows(Array.isArray(payload?.rows) ? payload.rows : []);
      setSummary(payload?.summary || { total: 0, callback: 0, voicemail: 0, recorded: 0, languages: {} });
      setPagination(payload?.pagination || { page, total: 0, totalPages: 1 });
      setReportInterval(payload?.reportInterval || '');
    } catch (requestError) {
      setRows([]);
      setSummary({ total: 0, callback: 0, voicemail: 0, recorded: 0, languages: {} });
      setPagination({ page: 1, total: 0, totalPages: 1 });
      setError(requestError.message || 'Report could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const defaults = initialFilters();
    loadReport(defaults, 1);
  }, [loadReport]);

  const updateFilter = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const exportExcel = async () => {
    if (!filters.startDate || !filters.endDate) {
      setError('Start Date and End Date are required.');
      return;
    }

    setExporting(true);
    setError('');
    try {
      const exportRows = [];
      let exportSummary = null;
      let exportInterval = '';
      let page = 1;
      let totalPages = 1;

      do {
        const params = new URLSearchParams({
          startDate: filters.startDate,
          endDate: filters.endDate,
          page: String(page),
          perPage: '200',
        });
        ['type', 'language', 'callerNumber'].forEach((key) => {
          const value = String(filters[key] || '').trim();
          if (value) params.set(key, value);
        });

        const response = await fetch(`${API_BASE}/reports/queue-contact-options?${params.toString()}`, {
          headers: authHeaders(),
        });
        const payload = await readResponse(response);
        exportRows.push(...(Array.isArray(payload?.rows) ? payload.rows : []));
        if (page === 1) {
          exportSummary = payload?.summary || summary;
          exportInterval = payload?.reportInterval || reportInterval;
        }
        totalPages = Math.max(1, Number(payload?.pagination?.totalPages || 1));
        page += 1;
      } while (page <= totalPages);

      if (!exportRows.length) {
        setError('No callback or voicemail records found for export.');
        return;
      }

      const summaryRows = [
        ['Callback / Voicemail Report'],
        ['Report Interval', exportInterval || `${filters.startDate} - ${filters.endDate}`],
        ['Type', filters.type || 'All'],
        ['Language', filters.language || 'All'],
        ['Caller Number', filters.callerNumber || 'All'],
        [],
        ['Metric', 'Total'],
        ['Total', Number(exportSummary?.total || 0)],
        ['Callback', Number(exportSummary?.callback || 0)],
        ['Voicemail', Number(exportSummary?.voicemail || 0)],
        ['Recorded', Number(exportSummary?.recorded || 0)],
        ['English', Number(exportSummary?.languages?.English || 0)],
        ['Urdu', Number(exportSummary?.languages?.Urdu || 0)],
        ['Sindhi', Number(exportSummary?.languages?.Sindhi || 0)],
        ['Punjabi', Number(exportSummary?.languages?.Punjabi || 0)],
        ['Unknown', Number(exportSummary?.languages?.Unknown || 0)],
      ];
      const detailRows = [
        ['Date / Time', 'Type', 'Language', 'Queue', 'Caller Number', 'Call ID', 'Status', 'Recording', 'Duration'],
        ...exportRows.map((row) => [
          formatDateTime(row.dateTime),
          row.type || '-',
          row.language || 'Unknown',
          row.queueName || '-',
          row.callerNumber || '-',
          row.callId || '-',
          row.status || '-',
          row.recordingAvailable ? 'Yes' : 'No',
          formatDuration(row.durationSeconds),
        ]),
      ];

      const workbook = XLSX.utils.book_new();
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows);
      const detailSheet = XLSX.utils.aoa_to_sheet(detailRows);
      summarySheet['!cols'] = [{ wch: 22 }, { wch: 42 }];
      detailSheet['!cols'] = [
        { wch: 22 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 18 },
        { wch: 22 }, { wch: 14 }, { wch: 12 }, { wch: 12 },
      ];
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
      XLSX.utils.book_append_sheet(workbook, detailSheet, 'Record Detail');
      XLSX.writeFile(workbook, `callback-voicemail-${filters.startDate}-${filters.endDate}.xlsx`);
    } catch (exportError) {
      setError(exportError.message || 'Excel export could not be created.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="cdn-report-shell">
      <header className="cdn-report-topbar no-print">
        <div>
          <strong>SLACC</strong>
          <span>Sindh Legal Advisory Call Center</span>
        </div>
        <div>
          <h1>Callback / Voicemail Report</h1>
          <p>Queue caller selections by language</p>
        </div>
        <div className="cdn-report-user-actions">
          <span>{username ? `${username}${roleName ? ` - ${roleName}` : ''}` : 'Report Module'}</span>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/backoffice')}>Back Office</button>
          <button type="button" className="cdn-btn danger" onClick={() => navigate('/logout')}>Logout</button>
        </div>
      </header>

      <section className="cdn-filter-panel no-print">
        <div className="daily-call-filter-grid">
          <label>
            <span>Start Date</span>
            <input type="date" name="startDate" value={filters.startDate} onChange={updateFilter} />
          </label>
          <label>
            <span>End Date</span>
            <input type="date" name="endDate" value={filters.endDate} onChange={updateFilter} />
          </label>
          <label>
            <span>Type</span>
            <select name="type" value={filters.type} onChange={updateFilter}>
              <option value="">All</option>
              <option value="callback">Callback</option>
              <option value="voicemail">Voicemail</option>
            </select>
          </label>
          <label>
            <span>Language</span>
            <select name="language" value={filters.language} onChange={updateFilter}>
              <option value="">All</option>
              <option value="English">English</option>
              <option value="Urdu">Urdu</option>
              <option value="Sindhi">Sindhi</option>
              <option value="Punjabi">Punjabi</option>
              <option value="Unknown">Unknown</option>
            </select>
          </label>
          <label>
            <span>Caller Number</span>
            <input name="callerNumber" value={filters.callerNumber} onChange={updateFilter} placeholder="All callers" />
          </label>
          <div className="cdn-filter-actions">
            <button type="button" className="cdn-btn primary" onClick={() => loadReport(filters, 1)} disabled={loading}>
              {loading ? 'Loading' : 'Show'}
            </button>
            <button type="button" className="cdn-btn success" onClick={exportExcel} disabled={loading || exporting}>
              {exporting ? 'Exporting' : 'Excel'}
            </button>
          </div>
        </div>
        {error ? <div className="cdn-alert">{error}</div> : null}
      </section>

      <main className="cdn-report-paper daily-call-report-paper">
        <section className="cdn-report-heading">
          <h2>Callback / Voicemail Report</h2>
          <p><strong>Report Interval:</strong> {reportInterval || `${filters.startDate} - ${filters.endDate}`}</p>
          <p><strong>Filters:</strong> {filters.type || 'All types'} / {filters.language || 'All languages'} / {filters.callerNumber || 'All callers'}</p>
        </section>

        <section className="daily-call-kpi-grid">
          <article><span>Total</span><strong>{Number(summary.total || 0).toLocaleString()}</strong></article>
          <article><span>Callback</span><strong>{Number(summary.callback || 0).toLocaleString()}</strong></article>
          <article><span>Voicemail</span><strong>{Number(summary.voicemail || 0).toLocaleString()}</strong></article>
          <article><span>Recorded</span><strong>{Number(summary.recorded || 0).toLocaleString()}</strong></article>
          <article><span>English</span><strong>{Number(summary.languages?.English || 0).toLocaleString()}</strong></article>
          <article><span>Urdu</span><strong>{Number(summary.languages?.Urdu || 0).toLocaleString()}</strong></article>
          <article><span>Sindhi</span><strong>{Number(summary.languages?.Sindhi || 0).toLocaleString()}</strong></article>
          <article><span>Punjabi</span><strong>{Number(summary.languages?.Punjabi || 0).toLocaleString()}</strong></article>
        </section>

        <section className="daily-call-table-section">
          <h3 className="daily-call-section-title">Record Detail</h3>
          <table className="daily-call-report-table">
            <thead>
              <tr>
                <th>Date / Time</th>
                <th>Type</th>
                <th>Language</th>
                <th>Queue</th>
                <th>Caller Number</th>
                <th>Call ID</th>
                <th>Status</th>
                <th>Recording</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? rows.map((row) => (
                <tr key={row.callStateLogId}>
                  <td>{formatDateTime(row.dateTime)}</td>
                  <td>{row.type || '-'}</td>
                  <td>{row.language || 'Unknown'}</td>
                  <td>{row.queueName || '-'}</td>
                  <td>{row.callerNumber || '-'}</td>
                  <td>{row.callId || '-'}</td>
                  <td>{row.status || '-'}</td>
                  <td>{row.recordingAvailable ? 'Yes' : 'No'}</td>
                  <td>{formatDuration(row.durationSeconds)}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="9" className="cdn-empty">{loading ? 'Loading records...' : 'No callback or voicemail records found.'}</td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </main>

      <section className="cdn-pager no-print">
        <button
          type="button"
          className="cdn-btn soft"
          disabled={loading || Number(pagination.page || 1) <= 1}
          onClick={() => loadReport(filters, Number(pagination.page || 1) - 1)}
        >
          Previous
        </button>
        <span>Page {pagination.page || 1} of {pagination.totalPages || 1} - {Number(pagination.total || 0).toLocaleString()} records</span>
        <button
          type="button"
          className="cdn-btn soft"
          disabled={loading || Number(pagination.page || 1) >= Number(pagination.totalPages || 1)}
          onClick={() => loadReport(filters, Number(pagination.page || 1) + 1)}
        >
          Next
        </button>
      </section>
    </div>
  );
};

export default QueueContactOptionReport;
